// Independent poll of the Skipper chat-check endpoint to surface the launch poll
// handle (`reportGeneration`) that the read-only lib/skipper poll consumer drops.
// It's the only in-session signal that a guided report was dispatched, so we use it
// to flip the client-only draft to a "generating" state. The real report + real id
// arrive on reload (see the guided-reports flow: server schema omits generation
// status and there is no in-session reports-list refetch).
import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { isGenerationHandleFresh } from '@aytm/app/stats/stores/guidedReportJobPoll'
import { schedulePoll } from '@aytm/app/stats/stores/pollDelay'

// Matches the backend chat idle poll interval (POLL_INTERVALS[:idle]); generation runs
// ~5–10 min, so a slower cadence than the report-status poller is fine.
const POLL_MS = 15000

// Shape stashed by ReportDispatcher#persist_generation_handle and advertised as
// `reportGeneration` on the chat poll response.
export interface GuidedReportGenerationHandle {
  generation_id?: string
  // id of the IN_PROGRESS report row the stat server creates before enqueueing the job
  report_id?: number
  job_id?: string
  polling_url?: string
  enqueued_reports?: number
  dispatched_at?: string
}

// The stat server writes the IN_PROGRESS report row before enqueueing the job, so the
// launch handle carries the real StatReport id. When present, the client-only draft adopts
// it and becomes the real report in-session instead of waiting for a reload.
export const generationReportId = (handle?: GuidedReportGenerationHandle | null): number | null => {
  const id = Number(handle?.report_id)
  return Number.isInteger(id) && 0 < id ? id : null
}

type StatsAssistant = {
  id: string
  chatID: string | number | null
  messages: { id: string }[]
  contextGetter: (() => { token: string; expire: number; survey_id?: number | null } | null) | null
}

const getStatsAssistant = (): StatsAssistant | null =>
  (window.$STATS?.skipperChatAssistants?.assistants?.find(a => 'stats' === a.id) as unknown as StatsAssistant) ?? null

export interface GuidedReportGenerationPoll {
  start(): void
  stop(): void
}

// Fires `onGeneration` once per generation the chat advertises for the active survey, and keeps
// watching afterwards — a session can dispatch several (create, then regenerate). `recovered` says
// the handle was already stored on the first poll, so it describes a generation that started before
// this poll did (the page bootstrap already reflects it) rather than one dispatched just now. The
// caller must call stop() on teardown.
export const createGuidedReportGenerationPoll = ({
  onGeneration
}: {
  onGeneration: (handle: GuidedReportGenerationHandle, meta: { recovered: boolean }) => void
}): GuidedReportGenerationPoll => {
  let cancelWait: (() => void) | null = null
  let controller: AbortController | null = null
  let stopped = false
  let polls = 0
  const seen = new Set<string>()

  const stop = (): void => {
    stopped = true
    cancelWait?.()
    cancelWait = null
    controller?.abort()
  }

  const schedule = (): void => {
    if (stopped) return
    cancelWait = schedulePoll(POLL_MS, () => void poll())
  }

  const poll = async (): Promise<void> => {
    if (stopped) return

    const assistant = getStatsAssistant()
    const chatId = assistant?.chatID
    const context = assistant?.contextGetter?.()
    // The chat may not be initialised yet (draft was just created) — retry next tick.
    if (!chatId || !context) return schedule()

    controller = new AbortController()
    try {
      // Mirror the chat's own check body so this poll is indistinguishable from it.
      const body = { domain: 'stats', context, ids: (assistant?.messages ?? []).map(m => m.id) }
      const response = await railsPostFetch(`/api/v1/skipper/chats/${chatId}/messages`, {
        body: JSON.stringify(body),
        signal: controller.signal
      })
      const data = response.ok ? await response.json() : null
      const handle: GuidedReportGenerationHandle | undefined = data?.reportGeneration
      polls += 1
      // Fire only on a fresh, not-yet-seen handle: the backend never clears the stored handle, so
      // a stale one advertises a long-completed generation — keep polling past it.
      const generationId = handle?.generation_id
      if (generationId && !seen.has(generationId) && isGenerationHandleFresh(handle)) {
        seen.add(generationId)
        onGeneration(handle, { recovered: 1 === polls })
      }
    } catch {
      // Transient (abort / network) — keep polling until stopped.
    }
    schedule()
  }

  return {
    start(): void {
      if (!stopped && null === cancelWait) void poll()
    },
    stop
  }
}
